(() => {
  window.ACStorefrontComponents?.destroy?.();

  const selector = '[data-ac-disclosure]';
  const listeners = [];
  const animations = new Map();
  const popoverClosures = new Map();
  const hoverTimers = new Map();
  const hoverPopover = window.matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)');
  const on = (target, type, listener, options) => {
    target.addEventListener(type, listener, options);
    listeners.push(() => target.removeEventListener(type, listener, options));
  };
  const disclosures = (root = document) => [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
  const summaryFor = (details) => details?.querySelector(':scope > summary');
  const panelFor = (details) => details?.querySelector(':scope > [data-ac-disclosure-panel]');
  const isInline = (details) => details?.classList.contains('ac-disclosure--inline');
  const isHoverPopover = (details) => details?.dataset.acDisclosureBehavior === 'hover-popover';
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reducedMotion = () => document.body?.dataset.acMotion === 'reduced' || motionQuery.matches;
  const sync = (details) => {
    const open = details.open && details.dataset.acDisclosureTarget !== 'closed';
    summaryFor(details)?.setAttribute('aria-expanded', String(open));
    if (isHoverPopover(details)) {
      const panel = panelFor(details);
      if (panel) {
        panel.inert = !open;
        panel.setAttribute('aria-hidden', String(!open));
      }
    }
  };
  const clearHoverTimer = (details) => {
    const timer = hoverTimers.get(details);
    if (timer) window.clearTimeout(timer);
    hoverTimers.delete(details);
  };

  const alignOpenedSummary = (details) => {
    if (details.dataset.acDisclosureAlign !== 'mobile' || !window.matchMedia('(max-width: 767px)').matches
      || !details.isConnected || details.closest('dialog, [role="dialog"]')) return;
    const summary = summaryFor(details);
    if (!summary) return;
    const offset = parseFloat(getComputedStyle(summary).scrollMarginBlockStart) || 0;
    const top = summary.getBoundingClientRect().top;
    if (Math.abs(top - offset) < 2) return;
    const target = window.scrollY + top - offset;
    const lenis = window.AcSmoothScroll?.lenis;
    if (lenis) {
      // Expansion changes the document height before Lenis' debounced observer runs.
      lenis.resize();
      lenis.scrollTo(target, { immediate: reducedMotion(), duration: 0.35 });
    } else window.scrollTo({ top: target, behavior: reducedMotion() ? 'instant' : 'smooth' });
  };

  const finishInline = (details, panel, open) => {
    panel.style.removeProperty('height');
    panel.style.removeProperty('opacity');
    panel.style.removeProperty('overflow');
    if (!open) details.open = false;
    details.dataset.acDisclosureTarget = open ? 'open' : 'closed';
    animations.delete(details);
    sync(details);
    if (open) alignOpenedSummary(details);
  };

  const finishPopover = (details, open, restoreFocus = false) => {
    popoverClosures.get(details)?.();
    popoverClosures.delete(details);
    if (!open) details.open = false;
    details.dataset.acDisclosureTarget = open ? 'open' : 'closed';
    sync(details);
    if (restoreFocus) summaryFor(details)?.focus({ preventScroll: true });
  };

  const setPopoverOpen = (details, open, restoreFocus = false) => {
    const panel = panelFor(details);
    const target = open ? 'open' : 'closed';
    if (!panel || details.dataset.acDisclosureTarget === target) return;
    popoverClosures.get(details)?.();
    popoverClosures.delete(details);
    // CSS alone owns the reveal. Keep native content rendered during its exit,
    // while the desired state immediately disables its input and expanded icon.
    if (open && !details.open) details.open = true;
    details.dataset.acDisclosureTarget = target;
    sync(details);
    if (restoreFocus) summaryFor(details)?.focus({ preventScroll: true });
    if (open) return;
    if (reducedMotion()) {
      finishPopover(details, open, restoreFocus);
      return;
    }
    const finish = () => finishPopover(details, false);
    const onEnd = (event) => {
      if (event.target === panel && event.propertyName === 'transform') finish();
    };
    panel.addEventListener('transitionend', onEnd);
    // An enter and leave in the same frame may produce no transitionend.
    const timeout = window.setTimeout(finish, 700);
    popoverClosures.set(details, () => {
      panel.removeEventListener('transitionend', onEnd);
      window.clearTimeout(timeout);
    });
  };

  const setInlineOpen = (details, open, restoreFocus = false) => {
    const panel = panelFor(details);
    const target = open ? 'open' : 'closed';
    if (!panel || (details.dataset.acDisclosureTarget === target && !animations.has(details))) return;
    animations.get(details)?.cancel();
    if (reducedMotion() || typeof panel.animate !== 'function') {
      details.open = open;
      finishInline(details, panel, open);
      if (restoreFocus) summaryFor(details)?.focus();
      return;
    }

    const currentHeight = details.open ? panel.getBoundingClientRect().height : 0;
    if (open && !details.open) details.open = true;
    details.dataset.acDisclosureTarget = target;
    panel.style.overflow = 'clip';
    const animation = panel.animate(
      [
        { height: `${currentHeight}px`, opacity: open ? 0 : 1 },
        { height: `${open ? panel.scrollHeight : 0}px`, opacity: open ? 1 : 0 },
      ],
      { duration: 280, easing: 'cubic-bezier(.22,1,.36,1)' },
    );
    animations.set(details, animation);
    animation.onfinish = () => {
      finishInline(details, panel, open);
      if (restoreFocus) summaryFor(details)?.focus();
    };
    animation.oncancel = () => {
      if (animations.get(details) === animation) animations.delete(details);
      panel.style.removeProperty('height');
      panel.style.removeProperty('opacity');
      panel.style.removeProperty('overflow');
    };
  };

  const close = (details, restoreFocus = false) => {
    if (!details?.open && details?.dataset.acDisclosureTarget !== 'open') return;
    clearHoverTimer(details);
    if (isInline(details)) {
      setInlineOpen(details, false, restoreFocus);
      return;
    }
    if (isHoverPopover(details) && hoverPopover.matches) {
      setPopoverOpen(details, false, restoreFocus);
      return;
    }
    details.open = false;
    details.dataset.acDisclosureTarget = 'closed';
    sync(details);
    if (restoreFocus) summaryFor(details)?.focus();
  };

  const open = (details) => {
    if (!details) return;
    clearHoverTimer(details);
    if (details.open && details.dataset.acDisclosureTarget === 'open' && !animations.has(details)) return;
    closeGroupSiblings(details);
    if (isHoverPopover(details) && hoverPopover.matches) {
      setPopoverOpen(details, true);
      return;
    }
    details.open = true;
    details.dataset.acDisclosureTarget = 'open';
    sync(details);
  };

  const closeGroupSiblings = (details) => {
    const group = details.dataset.acDisclosureGroup;
    if (!group) return;
    disclosures().forEach((candidate) => {
      if (candidate === details || candidate.dataset.acDisclosureGroup !== group) return;
      // Retire an older exit before switching again: only the current panel and
      // its immediate predecessor may paint during a rapid sweep across facets.
      if (popoverClosures.has(candidate)) finishPopover(candidate, false);
      else close(candidate);
    });
  };

  const onToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.matches(selector)) return;
    if (isHoverPopover(details) && hoverPopover.matches) {
      // Native toggle is queued and details stays open during a CSS exit.
      // It must never overwrite that exit's target or close the new sibling.
      if (popoverClosures.has(details)) return;
      if (details.open && details.dataset.acDisclosureTarget !== 'open') open(details);
      else if (!details.open) finishPopover(details, false);
      sync(details);
      return;
    }
    if (details.open) closeGroupSiblings(details);
    else clearHoverTimer(details);
    details.dataset.acDisclosureTarget = details.open ? 'open' : 'closed';
    sync(details);
  };

  const onClick = (event) => {
    const summary = event.target instanceof Element ? event.target.closest('summary') : null;
    const summaryDetails = summary?.parentElement;
    if (isHoverPopover(summaryDetails) && hoverPopover.matches && summary === summaryFor(summaryDetails)) {
      event.preventDefault();
      if (event.detail === 0 && summaryDetails.dataset.acDisclosureTarget === 'open') close(summaryDetails);
      else open(summaryDetails);
      return;
    }
    if (summaryDetails?.matches(`${selector}.ac-disclosure--inline`) && summary === summaryFor(summaryDetails)) {
      event.preventDefault();
      const opening = summaryDetails.dataset.acDisclosureTarget ? summaryDetails.dataset.acDisclosureTarget !== 'open' : !summaryDetails.open;
      if (opening) closeGroupSiblings(summaryDetails);
      setInlineOpen(summaryDetails, opening);
      return;
    }

    disclosures().forEach((details) => {
      if (!isInline(details) && details.open && !details.contains(event.target)) close(details);
    });
  };

  const onPointerOver = (event) => {
    if (!hoverPopover.matches || !(event.target instanceof Element)) return;
    const details = event.target.closest(`${selector}[data-ac-disclosure-behavior="hover-popover"]`);
    if (!details || (event.relatedTarget instanceof Node && details.contains(event.relatedTarget))) return;
    clearHoverTimer(details);
    open(details);
  };

  const onPointerOut = (event) => {
    if (!hoverPopover.matches || !(event.target instanceof Element)) return;
    const details = event.target.closest(`${selector}[data-ac-disclosure-behavior="hover-popover"]`);
    if (!details || (event.relatedTarget instanceof Node && details.contains(event.relatedTarget))) return;
    clearHoverTimer(details);
    const timer = window.setTimeout(() => {
      hoverTimers.delete(details);
      const keyboardFocus = details.querySelector(':focus-visible');
      const editingPanel = panelFor(details)?.contains(document.activeElement);
      if (!details.matches(':hover') && !keyboardFocus && !editingPanel) close(details);
    }, 45);
    hoverTimers.set(details, timer);
  };

  const onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const details = event.target instanceof Element ? event.target.closest(selector) : null;
    if (!details?.open) return;
    event.preventDefault();
    close(details, true);
  };

  const onChange = (event) => {
    const details = event.target instanceof Element ? event.target.closest(`${selector}[data-ac-disclosure-close-on-select]`) : null;
    if (details) close(details, true);
  };

  const syncRoot = (root = document) => disclosures(root).forEach((details) => {
    details.dataset.acDisclosureTarget = details.open ? 'open' : 'closed';
    sync(details);
  });
  const finishAnimation = (animation) => {
    const finish = animation.onfinish;
    animation.onfinish = null;
    animation.finish();
    finish?.();
  };
  const finishWithin = (root = document) => {
    for (const [details, animation] of animations) {
      if (details === root || root.contains(details)) finishAnimation(animation);
    }
    for (const details of popoverClosures.keys()) {
      if (details === root || root.contains(details)) finishPopover(details, false);
    }
    for (const details of hoverTimers.keys()) {
      if (details === root || root.contains(details)) clearHoverTimer(details);
    }
  };
  const syncMotion = () => { if (reducedMotion()) finishWithin(); };
  const motionObserver = new MutationObserver(syncMotion);
  motionObserver.observe(document.body, { attributes: true, attributeFilter: ['data-ac-motion'] });
  const boot = () => syncRoot();
  const destroy = () => {
    listeners.splice(0).reverse().forEach((remove) => remove());
    motionObserver.disconnect();
    animations.forEach(finishAnimation);
    for (const details of popoverClosures.keys()) finishPopover(details, false);
    hoverTimers.forEach((timer) => window.clearTimeout(timer));
    hoverTimers.clear();
  };

  on(document, 'toggle', onToggle, true);
  on(document, 'click', onClick);
  on(document, 'pointerover', onPointerOver);
  on(document, 'pointerout', onPointerOut);
  on(document, 'keydown', onKeydown);
  on(document, 'change', onChange);
  on(document, 'shopify:section:load', (event) => syncRoot(event.target));
  on(document, 'shopify:section:unload', (event) => finishWithin(event.target));
  on(motionQuery, 'change', syncMotion);
  on(window, 'pagehide', (event) => { if (!event.persisted) destroy(); });
  on(hoverPopover, 'change', () => disclosures().filter(isHoverPopover).forEach((details) => {
    clearHoverTimer(details);
    finishPopover(details, false);
  }));

  if (document.readyState === 'loading') on(document, 'DOMContentLoaded', boot, { once: true });
  else boot();

  window.ACStorefrontComponents = { destroy, sync: syncRoot };
})();
